// Módulo Empresa — 4.1: "Datos institucionales, contacto, redes sociales,
// horario". Solo edita `empresa_perfil` (más `empresa.nombre`, que es el
// nombre visible del negocio) — subdominio/plan/estado son de solo lectura
// acá (ver nota de cabecera en api/empresa-perfil.js), y se presentan como
// información de cuenta, no como campos editables falsos.
//
// RESPONSABILIDAD (arquitectura de módulos):
//   - Mi empresa  : información institucional real (identidad, descripción,
//                   historia, contacto, ubicación, horarios, redes).
//   - Página principal : contenido de portada (banners, categorías,
//                   productos destacados, propuesta, testimonios).
//   - Personalizar tienda : solo presentación/variantes visuales.
// Aquí NO viven Productos destacados ni Testimonios.

import { mostrarToast } from '@plataforma/ui';
import {
  esCorreoValido,
  escaparHtml,
  mensajeErrorAmigable,
  tieneFeature,
  FEATURE_DIRECTORIO_VIMOS,
  CATEGORIAS_TIENDA,
  validarRequisitosDirectorio,
  REQUISITOS_DIRECTORIO_ETIQUETA,
} from '@plataforma/utils';

import { subirLogoEmpresa } from '../api/almacenamiento.js';
import {
  actualizarNombreEmpresa,
  actualizarPerfilEmpresa,
  esUrlHttp,
  esWhatsappValido,
  normalizarHorario,
  normalizarWhatsapp,
  obtenerEmpresaActual,
} from '../api/empresa-perfil.js';
import { exigirSesionEmpresa, exigirEmpresaActiva } from '../guardas.js';

import { montarShell } from './shell.js';

const DIAS = [
  { clave: 'lunes', etiqueta: 'Lunes' },
  { clave: 'martes', etiqueta: 'Martes' },
  { clave: 'miercoles', etiqueta: 'Miércoles' },
  { clave: 'jueves', etiqueta: 'Jueves' },
  { clave: 'viernes', etiqueta: 'Viernes' },
  { clave: 'sabado', etiqueta: 'Sábado' },
  { clave: 'domingo', etiqueta: 'Domingo' },
];

const REDES = [
  { clave: 'facebook', etiqueta: 'Facebook' },
  { clave: 'instagram', etiqueta: 'Instagram' },
  { clave: 'tiktok', etiqueta: 'TikTok' },
  { clave: 'youtube', etiqueta: 'YouTube' },
];

const ETIQUETA_ESTADO_EMPRESA = {
  en_prueba: 'En prueba',
  activa: 'Activa',
  suspendida: 'Suspendida',
  dada_de_baja: 'Dada de baja',
};

const MAX_HISTORIA = 1000;

export async function vistaEmpresa(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/empresa');
  contenido.innerHTML = `<p class="pf-texto-suave">Cargando…</p>`;

  const { data: empresa, error } = await obtenerEmpresaActual();
  if (error) {
    contenido.innerHTML = `<p class="pf-campo__error">No se pudo cargar la información de tu empresa.</p>`;
    return;
  }

  if (!exigirEmpresaActiva(empresa)) return;

  const perfil = empresa.perfil ?? {};
  const horario = normalizarHorario(perfil.horario);
  const esElegibleDirectorio = tieneFeature(empresa, FEATURE_DIRECTORIO_VIMOS);

  contenido.innerHTML = `
    <div class="pf-empresa">
      <h1>Mi empresa</h1>
      <p class="pf-texto-suave pf-empresa__intro">
        Información institucional y real de tu negocio. Se reutiliza en tu tienda:
        contacto, pie de página y presentación.
      </p>

      <!-- Resumen superior -->
      <div class="pf-tarjeta pf-empresa__resumen">
        <div class="pf-empresa__resumen-marca">
          <div class="pf-empresa__logo-mini" id="pf-empresa-logo-mini">
            ${
              perfil.logo_url
                ? `<img src="${escaparHtml(perfil.logo_url)}" alt="${escaparHtml(empresa.nombre)}" />`
                : `<span aria-hidden="true">${iniciales(empresa.nombre)}</span>`
            }
          </div>
          <div class="pf-empresa__resumen-info">
            <strong class="pf-empresa__resumen-nombre">${escaparHtml(empresa.nombre)}</strong>
            <span class="pf-texto-suave">${escaparHtml(empresa.subdominio)}.vimosdigital.com</span>
          </div>
        </div>
        <div class="pf-empresa__resumen-meta">
          <span class="pf-empresa__meta-pill">Plan ${escaparHtml(empresa.plan?.nombre ?? '—')}</span>
          <pf-badge-estado tono="${empresa.estado === 'activa' ? 'exito' : 'alerta'}">
            ${ETIQUETA_ESTADO_EMPRESA[empresa.estado] ?? empresa.estado}
          </pf-badge-estado>
        </div>
      </div>

      <form id="pf-form-empresa" novalidate data-logo-url="${escaparHtml(perfil.logo_url ?? '')}">

        <!-- IDENTIDAD DEL NEGOCIO -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Identidad del negocio</h2>
            <p>Logo y nombre que identifican tu marca en cabecera, editor y tienda.</p>
          </header>

          <div class="pf-empresa__identidad">
            <div class="pf-empresa__logo-editor">
              <div class="pf-empresa__logo-preview" id="pf-empresa-logo-preview">
                ${
                  perfil.logo_url
                    ? `<img src="${escaparHtml(perfil.logo_url)}" alt="Logo de la empresa" />`
                    : `<span class="pf-texto-suave" aria-hidden="true">Sin logo</span>`
                }
              </div>
              <div class="pf-empresa__logo-acciones">
                <label class="pf-boton pf-boton--secundario pf-boton--chico" style="cursor:pointer;margin:0;">
                  Cambiar imagen
                  <input type="file" id="pf-empresa-logo-input" accept="image/jpeg,image/png,image/webp" hidden />
                </label>
                <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="pf-empresa-logo-restablecer" hidden>
                  Restablecer
                </button>
                <button type="button" class="pf-boton pf-boton--peligro pf-boton--chico" id="pf-empresa-logo-eliminar" ${perfil.logo_url ? '' : 'disabled'}>
                  Eliminar
                </button>
              </div>
              <p class="pf-texto-suave pf-empresa__logo-ayuda" id="pf-empresa-logo-ayuda">
                Se muestra en la cabecera y el pie de la tienda. JPG, PNG o WEBP.
              </p>
            </div>

            <div class="pf-empresa__identidad-campos">
              <div class="pf-campo">
                <label for="pf-empresa-nombre">Nombre de la empresa</label>
                <input type="text" id="pf-empresa-nombre" value="${escaparHtml(empresa.nombre)}" maxlength="80" />
                <small>Es el nombre que ven tus clientes en la tienda.</small>
              </div>
              <div class="pf-campo pf-campo--solo-lectura">
                <label for="pf-empresa-subdominio">Dominio de tu tienda</label>
                <div class="pf-campo__input pf-campo__input--solo-lectura">${escaparHtml(empresa.subdominio)}.vimosdigital.com</div>
                <small>No se puede editar desde aquí. Para cambiar el dominio, contáctanos y lo gestionamos por ti.</small>
              </div>
            </div>
          </div>
        </section>

        <!-- SOBRE LA EMPRESA -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Sobre la empresa</h2>
            <p>Una presentación breve y la historia de tu marca.</p>
          </header>
          <div class="pf-empresa__sobre">
            <div class="pf-campo">
              <label for="pf-empresa-descripcion">Descripción corta</label>
              <textarea id="pf-empresa-descripcion" rows="2" maxlength="300">${escaparTextarea(perfil.descripcion ?? '')}</textarea>
              <small>Presentación breve del negocio. Se usa en el pie de página de la tienda y en el resumen de contacto.</small>
            </div>
            <div class="pf-campo">
              <label for="pf-empresa-historia">Historia de tu empresa</label>
              <textarea id="pf-empresa-historia" rows="5" maxlength="${MAX_HISTORIA}"
                placeholder="Cuenta cómo nació tu negocio, qué lo hace diferente y qué hay detrás de tu marca.">${escaparTextarea(perfil.historia ?? '')}</textarea>
              <div class="pf-empresa__contador">
                <span class="pf-texto-suave">Uso interno: sección informativa de tu empresa.</span>
                <span id="pf-empresa-historia-contador">${(perfil.historia ?? '').length} / ${MAX_HISTORIA}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- INFORMACIÓN DE CONTACTO -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Información de contacto</h2>
            <p>Cómo pueden escribirte tus clientes. Esto alimenta el botón de contacto y el pie de página.</p>
          </header>
          <div class="pf-empresa__grid pf-empresa__grid--2">
            <div class="pf-campo">
              <label for="pf-empresa-whatsapp">WhatsApp</label>
              <input type="text" id="pf-empresa-whatsapp" inputmode="tel" value="${escaparHtml(perfil.whatsapp ?? '')}"
                placeholder="+51 987 654 321" />
              <small>Incluye el código de país, por ejemplo +51987654321. Lo usamos para el botón de contacto.</small>
              <p class="pf-campo__error pf-empresa__campo-error" id="pf-empresa-whatsapp-error" hidden></p>
            </div>
            <div class="pf-campo">
              <label for="pf-empresa-telefono">Teléfono <span class="pf-texto-suave">(opcional)</span></label>
              <input type="text" id="pf-empresa-telefono" inputmode="tel" value="${escaparHtml(perfil.telefono ?? '')}"
                placeholder="01 234 5678" />
              <small>Si es distinto a WhatsApp. Independiente y opcional.</small>
            </div>
            <div class="pf-campo">
              <label for="pf-empresa-correo">Correo de contacto</label>
              <input type="email" id="pf-empresa-correo" value="${escaparHtml(perfil.correo ?? '')}" />
              <p class="pf-campo__error pf-empresa__campo-error" id="pf-empresa-correo-error" hidden></p>
            </div>
          </div>
        </section>

        <!-- UBICACIÓN -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Ubicación</h2>
            <p>Datos de tu local físico, útiles para tiendas que atienden presencialmente.</p>
          </header>
          <div class="pf-empresa__grid pf-empresa__grid--2">
            <div class="pf-campo pf-empresa__campo-ancho">
              <label for="pf-empresa-direccion">Dirección</label>
              <input type="text" id="pf-empresa-direccion" value="${escaparHtml(perfil.direccion ?? '')}" placeholder="Av. Principal 123" />
            </div>
            <div class="pf-campo">
              <label for="pf-empresa-ciudad">Ciudad</label>
              <input type="text" id="pf-empresa-ciudad" value="${escaparHtml(perfil.ciudad ?? '')}" placeholder="Lima" />
            </div>
            <div class="pf-campo">
              <label for="pf-empresa-region">Región / departamento <span class="pf-texto-suave">(opcional)</span></label>
              <input type="text" id="pf-empresa-region" value="${escaparHtml(perfil.region ?? '')}" placeholder="Lima" />
            </div>
            <div class="pf-campo">
              <label for="pf-empresa-pais">País</label>
              <input type="text" id="pf-empresa-pais" value="${escaparHtml(perfil.pais ?? '')}" placeholder="Perú" />
            </div>
            <div class="pf-campo pf-empresa__campo-ancho">
              <label for="pf-empresa-mapa">Enlace de ubicación (Google Maps) <span class="pf-texto-suave">(opcional)</span></label>
              <input type="url" id="pf-empresa-mapa" value="${escaparHtml(perfil.mapa_url ?? '')}"
                placeholder="https://maps.google.com/?q=..." />
              <small>Un enlace a tu ubicación. Se usa en el pie de página si está habilitado el bloque de contacto.</small>
              <p class="pf-campo__error pf-empresa__campo-error" id="pf-empresa-mapa-error" hidden></p>
            </div>
          </div>
        </section>

        <!-- HORARIOS DE ATENCIÓN -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Horarios de atención</h2>
            <p>Define cuándo atiendes. Ideal para tiendas con local físico.</p>
          </header>

          <label class="pf-empresa__toggle">
            <input type="checkbox" id="pf-empresa-mostrar-horario" ${perfil.mostrar_horario !== false ? 'checked' : ''} />
            <span class="pf-empresa__toggle-desc">
              Mostrar horario de atención en la tienda
              <small class="pf-texto-suave">Desactívalo si trabajas 100% online.</small>
            </span>
          </label>

          <div class="pf-empresa__horario" id="pf-empresa-horario">
            ${DIAS.map(
              (d) => `
              <div class="pf-fila pf-empresa__dia" style="align-items: center; gap: 10px;">
                <label class="pf-empresa__dia-label">
                  <input type="checkbox" class="pf-empresa-dia-abierto" data-dia="${d.clave}" ${horario[d.clave].cerrado ? '' : 'checked'} />
                  ${d.etiqueta}
                </label>
                <input type="time" class="pf-empresa-dia-abre" data-dia="${d.clave}" value="${horario[d.clave].abre}" ${horario[d.clave].cerrado ? 'disabled' : ''} />
                <span class="pf-texto-suave">a</span>
                <input type="time" class="pf-empresa-dia-cierra" data-dia="${d.clave}" value="${horario[d.clave].cierra}" ${horario[d.clave].cerrado ? 'disabled' : ''} />
              </div>`,
            ).join('')}
          </div>
        </section>

        <!-- REDES SOCIALES -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Redes sociales</h2>
            <p>Opcionales. Se usan en el pie de página y en contacto sin duplicar información: este es el único lugar donde se guardan.</p>
          </header>
          <div class="pf-empresa__grid pf-empresa__grid--2">
            ${REDES.map(
              (r) => `
              <div class="pf-campo">
                <label for="pf-empresa-${r.clave}">
                  <span class="pf-empresa__red">
                    <span class="pf-empresa__red-nombre">${r.etiqueta}</span>
                    <span class="pf-empresa__red-estado ${perfil[r.clave] ? 'pf-empresa__red-estado--activa' : ''}"
                      id="pf-empresa-${r.clave}-estado" data-estado="${perfil[r.clave] ? '1' : '0'}">
                      ${perfil[r.clave] ? 'Configurada' : 'Sin configurar'}
                    </span>
                  </span>
                </label>
                <input type="url" class="pf-empresa-red" data-red="${r.clave}" id="pf-empresa-${r.clave}" value="${escaparHtml(perfil[r.clave] ?? '')}"
                  placeholder="https://${r.clave}.com/tu-usuario" />
                <p class="pf-campo__error pf-empresa__campo-error" id="pf-empresa-${r.clave}-error" hidden></p>
              </div>`,
            ).join('')}
          </div>
        </section>

        ${
          esElegibleDirectorio
            ? `
        <!-- DIRECTORIO VIMOS -->
        <section class="pf-tarjeta pf-empresa__seccion">
          <header class="pf-empresa__seccion-titulo">
            <h2>Directorio de Vimos Digital</h2>
            <p>
              Tu plan incluye aparecer en el directorio público de tiendas de Vimos. Para
              activarlo, completa categoría, ciudad, logo y descripción de tu tienda; luego
              actívalo o desactívalo cuando quieras.
            </p>
          </header>
          <div class="pf-campo">
            <label for="pf-empresa-categoria">Categoría de la tienda</label>
            <select id="pf-empresa-categoria">
              <option value="">Selecciona una categoría…</option>
              ${CATEGORIAS_TIENDA.map(
                (c) =>
                  `<option value="${c.valor}" ${perfil.categoria === c.valor ? 'selected' : ''}>${c.etiqueta}</option>`,
              ).join('')}
            </select>
            <p class="pf-texto-suave">La categoría es la sección del directorio en la que aparecerás.</p>
          </div>
          <label class="pf-empresa__toggle">
            <input type="checkbox" id="pf-empresa-mostrar-en-directorio" ${perfil.is_visible_in_directory ? 'checked' : ''} />
            <span class="pf-empresa__toggle-desc">
              Mostrar mi tienda en el directorio de Vimos Digital
              <small class="pf-texto-suave">
                Al activarlo, tu tienda (nombre, logo, ciudad, descripción y categoría)
                podrá aparecer en el directorio público. Puedes desactivarlo en cualquier
                momento.
              </small>
            </span>
          </label>
          <p class="pf-campo__error pf-empresa__campo-error" id="pf-empresa-directorio-error" hidden></p>
        </section>
        `
            : ''
        }

        <p class="pf-campo__error" id="pf-empresa-error" hidden></p>

        <div class="pf-fila pf-empresa__guardar" style="justify-content: flex-end;">
          <button type="submit" class="pf-boton pf-boton--primario" id="pf-empresa-submit">Guardar cambios</button>
        </div>
      </form>
    </div>
  `;

  cablearHistoriaContador(contenido);
  cablearLogo(contenido, empresa, perfil.logo_url ?? null);
  cablearHorario(contenido);
  cablearDirectorio(contenido, perfil);
  prepararValidacionRedes(contenido);
  cablearFormulario(contenido, empresa, esElegibleDirectorio, perfil);
}

function horarioVacio() {
  const objeto = {};
  for (const d of DIAS) objeto[d.clave] = { abre: '09:00', cierra: '18:00', cerrado: false };
  return objeto;
}

function iniciales(nombre) {
  return String(nombre ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
}

function escaparTextarea(valor) {
  return String(valor ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function cablearHistoriaContador(contenido) {
  const historia = contenido.querySelector('#pf-empresa-historia');
  if (!historia) return;
  const contador = contenido.querySelector('#pf-empresa-historia-contador');
  historia.addEventListener('input', () => {
    contador.textContent = `${historia.value.length} / ${MAX_HISTORIA}`;
  });
}

// Logo: fuente única de verdad `empresa_perfil.logo_url`. El cambio se sube y
// persiste de inmediato (con preview instantánea), igual que en
// Personalizar tienda — no hay una segunda fuente.
function cablearLogo(contenido, empresa, logoGuardado) {
  const input = contenido.querySelector('#pf-empresa-logo-input');
  const botonRestablecer = contenido.querySelector('#pf-empresa-logo-restablecer');
  const botonEliminar = contenido.querySelector('#pf-empresa-logo-eliminar');
  const preview = contenido.querySelector('#pf-empresa-logo-preview');
  const mini = contenido.querySelector('#pf-empresa-logo-mini');
  const ayuda = contenido.querySelector('#pf-empresa-logo-ayuda');

  let logoActual = logoGuardado;
  let logoPendiente = null; // URL provisional en preview antes de guardar

  function pintarPreview(url) {
    if (url) {
      preview.innerHTML = `<img src="${escaparHtml(url)}" alt="Logo de la empresa" />`;
    } else {
      preview.innerHTML = `<span class="pf-texto-suave" aria-hidden="true">Sin logo</span>`;
    }
  }

  function pintarMini(url) {
    mini.innerHTML = url
      ? `<img src="${escaparHtml(url)}" alt="${escaparHtml(empresa.nombre)}" />`
      : `<span aria-hidden="true">${iniciales(empresa.nombre)}</span>`;
  }

  function setPendiente(url) {
    logoPendiente = url;
    botonRestablecer.hidden = !logoPendiente;
  }

  input.addEventListener('change', async () => {
    const archivo = input.files?.[0];
    if (!archivo) return;
    const urlPrevia = URL.createObjectURL(archivo);
    pintarPreview(urlPrevia);
    setPendiente(urlPrevia);
    ayuda.textContent = 'Guardando logo…';

    const { url, error } = await subirLogoEmpresa(empresa.id, archivo);
    URL.revokeObjectURL(urlPrevia);
    if (error) {
      ayuda.textContent = error.message ?? 'No se pudo subir el logo.';
      pintarPreview(logoActual);
      setPendiente(null);
      return;
    }

    const { error: errorPersistencia } = await actualizarPerfilEmpresa({ logo_url: url });
    if (errorPersistencia) {
      ayuda.textContent = mensajeErrorAmigable(errorPersistencia, 'No se pudo guardar el logo.');
      pintarPreview(logoActual);
      setPendiente(null);
      return;
    }

    logoActual = url;
    contenido.querySelector('#pf-form-empresa').dataset.logoUrl = url;
    pintarPreview(url);
    pintarMini(url);
    setPendiente(null);
    botonEliminar.disabled = false;
    ayuda.textContent = 'Logo actualizado. Se verá en cabecera, pie y editor tras publicar.';
    mostrarToast('Logo actualizado.', { tono: 'exito' });
  });

  botonRestablecer.addEventListener('click', () => {
    pintarPreview(logoActual);
    setPendiente(null);
    ayuda.textContent = 'Se mantiene el logo guardado.';
  });

  botonEliminar.addEventListener('click', async () => {
    if (!window.confirm('¿Eliminar el logo de tu tienda?')) return;
    botonEliminar.disabled = true;
    ayuda.textContent = 'Eliminando logo…';
    const { error } = await actualizarPerfilEmpresa({ logo_url: null });
    if (error) {
      ayuda.textContent = mensajeErrorAmigable(error, 'No se pudo eliminar el logo.');
      botonEliminar.disabled = false;
      return;
    }
    logoActual = null;
    contenido.querySelector('#pf-form-empresa').dataset.logoUrl = '';
    pintarPreview(null);
    pintarMini(null);
    setPendiente(null);
    botonEliminar.disabled = true;
    ayuda.textContent = 'Logo eliminado.';
    mostrarToast('Logo eliminado.', { tono: 'exito' });
  });
}

function cablearHorario(contenido) {
  const toggle = contenido.querySelector('#pf-empresa-mostrar-horario');
  const horario = contenido.querySelector('#pf-empresa-horario');

  function actualizar() {
    horario.style.display = toggle.checked ? '' : 'none';
  }
  if (toggle) toggle.addEventListener('change', actualizar);
  actualizar();

  contenido.querySelectorAll('.pf-empresa-dia-abierto').forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      const dia = checkbox.dataset.dia;
      const deshabilitar = !checkbox.checked;
      contenido.querySelector(`.pf-empresa-dia-abre[data-dia="${dia}"]`).disabled = deshabilitar;
      contenido.querySelector(`.pf-empresa-dia-cierra[data-dia="${dia}"]`).disabled = deshabilitar;
    });
  });
}

// Marca cada red como "Configurada"/"Sin configurar" según tenga URL.
function prepararValidacionRedes(contenido) {
  contenido.querySelectorAll('.pf-empresa-red').forEach((input) => {
    input.addEventListener('input', () => {
      const estado = contenido.querySelector(`#pf-empresa-${input.dataset.red}-estado`);
      const activa = Boolean(input.value.trim());
      estado.dataset.estado = activa ? '1' : '0';
      estado.textContent = activa ? 'Configurada' : 'Sin configurar';
      estado.classList.toggle('pf-empresa__red-estado--activa', activa);
    });
  });
}

// Directorio: se necesita categoría, ciudad, logo y descripción para activar
// el toggle. Sin requisitos completos la activación se REVIERTE y se muestra
// solo lo que falta (regla central en packages/utils/src/vimos.js,
// `validarRequisitosDirectorio`); la sync también fuerza
// is_visible_in_directory=false (mismo validador en la capa pura).
function cablearDirectorio(contenido, perfil) {
  const select = contenido.querySelector('#pf-empresa-categoria');
  const toggle = contenido.querySelector('#pf-empresa-mostrar-en-directorio');
  const errorEl = contenido.querySelector('#pf-empresa-directorio-error');
  if (!select || !toggle) return;

  // Perfil candidato en vivo: ciudad/descripción se leen de los campos del
  // formulario (el usuario pudo escribirlos sin guardar), categoría del
  // select; el logo se lee del atributo que `cablearLogo` mantiene al
  // sincronizar (siempre el último guardado).
  function perfilCandidato() {
    return {
      categoria: select.value,
      ciudad: contenido.querySelector('#pf-empresa-ciudad')?.value.trim(),
      logo_url: contenido.querySelector('#pf-form-empresa')?.dataset.logoUrl,
      descripcion: contenido.querySelector('#pf-empresa-descripcion')?.value.trim(),
    };
  }

  function mostrarError(mensaje) {
    errorEl.textContent = mensaje;
    errorEl.hidden = false;
  }
  function ocultarError() {
    errorEl.hidden = true;
  }

  function mensajeFaltantes(faltantes) {
    const etiquetas = faltantes.map(
      (clave) => `«${REQUISITOS_DIRECTORIO_ETIQUETA[clave] ?? clave}»`,
    );
    return `Para mostrar tu tienda en el directorio completa: ${etiquetas.join(', ')}.`;
  }

  function mensajeSiIncompleto() {
    const { valido, faltantes } = validarRequisitosDirectorio(perfilCandidato());
    return valido ? null : mensajeFaltantes(faltantes);
  }

  // Estado inicial con consentimiento activo pero requisitos incompletos:
  // se informa al dueño (la tienda aún no aparece; no se altera el dato).
  if (perfil.is_visible_in_directory) {
    const incompleto = mensajeSiIncompleto();
    if (incompleto) {
      mostrarError(`Tu tienda aún no aparece en el directorio. ${incompleto}`);
    }
  }

  select.addEventListener('change', () => {
    if (!toggle.checked) {
      ocultarError();
      return;
    }
    const incompleto = mensajeSiIncompleto();
    if (incompleto) {
      toggle.checked = false;
      mostrarError(incompleto);
    } else {
      ocultarError();
    }
  });

  toggle.addEventListener('change', () => {
    if (toggle.checked) {
      const incompleto = mensajeSiIncompleto();
      if (incompleto) {
        toggle.checked = false;
        mostrarError(incompleto);
        select.focus();
        return;
      }
    }
    ocultarError();
  });
}

function cablearFormulario(contenido, empresa, esElegibleDirectorio, perfil) {
  const form = contenido.querySelector('#pf-form-empresa');
  const errorEl = contenido.querySelector('#pf-empresa-error');
  const submitEl = contenido.querySelector('#pf-empresa-submit');
  const nombreInput = contenido.querySelector('#pf-empresa-nombre');

  form.addEventListener('submit', async (evento) => {
    evento.preventDefault();
    errorEl.hidden = true;
    ocultarErroresCampo(contenido);

    const nuevoNombre = nombreInput.value.trim();
    if (!nuevoNombre) {
      mostrarErrorCampo(contenido, '#pf-empresa-nombre', 'El nombre de la empresa es obligatorio.');
      return;
    }

    const whatsapp = normalizarWhatsapp(contenido.querySelector('#pf-empresa-whatsapp').value);
    if (whatsapp && !esWhatsappValido(whatsapp)) {
      mostrarErrorCampo(
        contenido,
        '#pf-empresa-whatsapp',
        'Número de WhatsApp no válido. Usa el código de país, por ejemplo +51987654321.',
      );
      return;
    }

    const correo = contenido.querySelector('#pf-empresa-correo').value.trim();
    if (correo && !esCorreoValido(correo)) {
      mostrarErrorCampo(contenido, '#pf-empresa-correo', 'El correo de contacto no es válido.');
      return;
    }

    const mapa = contenido.querySelector('#pf-empresa-mapa').value.trim();
    if (mapa && !esUrlHttp(mapa)) {
      mostrarErrorCampo(
        contenido,
        '#pf-empresa-mapa',
        'El enlace de ubicación debe ser una URL válida (http/https).',
      );
      return;
    }

    for (const r of REDES) {
      const url = contenido.querySelector(`#pf-empresa-${r.clave}`).value.trim();
      if (url && !esUrlHttp(url)) {
        mostrarErrorCampo(
          contenido,
          `#pf-empresa-${r.clave}`,
          `El enlace de ${r.etiqueta} debe ser una URL válida (http/https).`,
        );
        return;
      }
    }

    const mostrarHorario = contenido.querySelector('#pf-empresa-mostrar-horario').checked;
    const horario = horarioVacio();
    for (const d of DIAS) {
      const abierto = contenido.querySelector(
        `.pf-empresa-dia-abierto[data-dia="${d.clave}"]`,
      ).checked;
      horario[d.clave] = {
        cerrado: !abierto,
        abre: contenido.querySelector(`.pf-empresa-dia-abre[data-dia="${d.clave}"]`).value,
        cierra: contenido.querySelector(`.pf-empresa-dia-cierra[data-dia="${d.clave}"]`).value,
      };
    }

    const cambios = {
      descripcion: contenido.querySelector('#pf-empresa-descripcion').value.trim() || null,
      historia: contenido.querySelector('#pf-empresa-historia').value.trim() || null,
      whatsapp: whatsapp || null,
      telefono: contenido.querySelector('#pf-empresa-telefono').value.trim() || null,
      correo: correo || null,
      direccion: contenido.querySelector('#pf-empresa-direccion').value.trim() || null,
      ciudad: contenido.querySelector('#pf-empresa-ciudad').value.trim() || null,
      region: contenido.querySelector('#pf-empresa-region').value.trim() || null,
      pais: contenido.querySelector('#pf-empresa-pais').value.trim() || null,
      mapa_url: mapa || null,
      mostrar_horario: mostrarHorario,
      horario,
    };
    if (esElegibleDirectorio) {
      const selectCategoria = contenido.querySelector('#pf-empresa-categoria');
      const toggleDirectorio = contenido.querySelector('#pf-empresa-mostrar-en-directorio');
      if (selectCategoria && toggleDirectorio) {
        // Re-validación al guardar con la MISMA regla central: si el dueño
        // intenta activar el directorio con requisitos incompletos (categoría,
        // ciudad, logo o descripción) se frena el guardado, se revierte el
        // toggle y se listan solo los faltantes — nunca se persiste true.
        const perfilCandidato = {
          categoria: selectCategoria.value,
          ciudad: cambios.ciudad,
          logo_url:
            contenido.querySelector('#pf-form-empresa')?.dataset.logoUrl ??
            perfil?.logo_url ??
            null,
          descripcion: cambios.descripcion,
        };
        const { valido, faltantes } = validarRequisitosDirectorio(perfilCandidato);
        if (toggleDirectorio.checked && !valido) {
          toggleDirectorio.checked = false;
          const directorioError = contenido.querySelector('#pf-empresa-directorio-error');
          if (directorioError) {
            const etiquetas = faltantes.map(
              (clave) => `«${REQUISITOS_DIRECTORIO_ETIQUETA[clave] ?? clave}»`,
            );
            directorioError.textContent = `Para mostrar tu tienda en el directorio completa: ${etiquetas.join(', ')}.`;
            directorioError.hidden = false;
          }
          selectCategoria.focus();
          return;
        }
        cambios.categoria = selectCategoria.value || null;
        cambios.is_visible_in_directory = toggleDirectorio.checked;
      }
    }
    for (const r of REDES) {
      cambios[r.clave] = contenido.querySelector(`#pf-empresa-${r.clave}`).value.trim() || null;
    }

    submitEl.disabled = true;
    submitEl.textContent = 'Guardando…';

    const tareas = [];
    if (nuevoNombre !== empresa.nombre) {
      tareas.push(actualizarNombreEmpresa(nuevoNombre));
    }
    tareas.push(actualizarPerfilEmpresa(cambios));

    const resultados = await Promise.all(tareas);
    const errorGuardado = resultados.find((res) => res.error)?.error ?? null;

    submitEl.disabled = false;
    submitEl.textContent = 'Guardar cambios';

    if (errorGuardado) {
      errorEl.textContent = mensajeErrorAmigable(
        errorGuardado,
        'No se pudieron guardar los datos de la empresa.',
      );
      errorEl.hidden = false;
      return;
    }

    mostrarToast('Datos de la empresa actualizados.', { tono: 'exito' });
  });
}

function ocultarErroresCampo(contenido) {
  contenido.querySelectorAll('.pf-empresa__campo-error').forEach((el) => {
    el.hidden = true;
  });
}

function mostrarErrorCampo(contenido, selector, mensaje) {
  const input = contenido.querySelector(selector);
  const errorEl = contenido.querySelector(`#${selector.slice(1)}-error`);
  if (input) input.focus();
  if (errorEl && errorEl.id !== 'pf-empresa-error') {
    errorEl.textContent = mensaje;
    errorEl.hidden = false;
    return;
  }
  const gen = contenido.querySelector('#pf-empresa-error');
  gen.textContent = mensaje;
  gen.hidden = false;
}
